import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  FilterList,
  GetApp,
  Business,
  LocationOn,
  Phone,
  Email,
  Language,
  Settings,
  Security,
  Notifications,
  Payment,
  Storage,
  CloudUpload,
  Backup,
  Update,
  CheckCircle,
  Warning,
  Error,
  Info,
  PersonAdd,
  Group,
} from '@mui/icons-material';
import companyService from '../services/companyService';
import { useNavigate } from 'react-router-dom';

interface Company {
  id: string;
  name: string;
  legalName: string;
  taxId: string;
  industry: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  contact: {
    phone: string;
    email: string;
    website: string;
  };
  logo?: string;
  status: 'active' | 'inactive' | 'suspended';
  subscription: {
    plan: string;
    status: 'active' | 'expired' | 'trial';
    expiryDate: string;
    users: number;
    maxUsers: number;
  };
  settings: {
    timezone: string;
    currency: string;
    language: string;
    dateFormat: string;
    notifications: boolean;
    backupEnabled: boolean;
    twoFactorAuth: boolean;
  };
  createdDate: string;
  lastUpdated: string;
  totalUsers: number;
  totalProjects: number;
  storageUsed: number;
  storageLimit: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}



const CompanyManagement: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const companiesData = await companyService.getAllCompanies();
        setCompanies(companiesData);
      } catch (error) {
        console.error('Error fetching companies:', error);
        // Keep companies as empty array on error
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  // Ensure companies is always an array for safe operations
  const companiesArray = Array.isArray(companies) ? companies : [];
  
  const filteredCompanies = companiesArray.filter((company) => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.legalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.industry.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalCompanies = companiesArray.length;
  const activeCompanies = companiesArray.filter(c => c.status === 'active').length;
  const suspendedCompanies = companiesArray.filter(c => c.status === 'suspended').length;
  const totalUsers = companiesArray.reduce((sum, c) => sum + (c?.totalUsers || 0), 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'suspended': return 'error';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const getSubscriptionStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'trial': return 'info';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatStorage = (gb: number) => {
    return `${gb.toFixed(1)} GB`;
  };

  const getStoragePercentage = (used: number, limit: number) => {
    return Math.round((used / limit) * 100);
  };

  const handleViewCompany = (company: Company) => {
    setSelectedCompany(company);
    setViewDialogOpen(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Company Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ bgcolor: 'primary.main' }}
          onClick={() => navigate('/company-management/create')}
        >
          Add Company
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Companies" icon={<Business />} />
          <Tab label="System Settings" icon={<Settings />} />
          <Tab label="Security" icon={<Security />} />
          <Tab label="Backup & Storage" icon={<Storage />} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
        {/* Summary Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Card sx={{ minWidth: 200, flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Business color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Companies
                  </Typography>
                  <Typography variant="h5">
                    {totalCompanies}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200, flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Companies
                  </Typography>
                  <Typography variant="h5">
                    {activeCompanies}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200, flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Group color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h5">
                    {totalUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200, flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Warning color="error" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Suspended
                  </Typography>
                  <Typography variant="h5">
                    {suspendedCompanies}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Filters and Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search companies..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              sx={{ ml: 'auto' }}
            >
              More Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<GetApp />}
            >
              Export
            </Button>
          </Box>
        </Paper>

        {/* Companies Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Storage</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCompanies.map((company) => {
                  const storagePercentage = getStoragePercentage(company.storageUsed, company.storageLimit);
                  
                  return (
                    <TableRow key={company.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {company.name.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {company.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {company.legalName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {company.industry}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email sx={{ fontSize: 16 }} />
                            {company.contact.email}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Phone sx={{ fontSize: 16 }} />
                            {company.contact.phone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {company.subscription.plan}
                          </Typography>
                          <Chip
                            label={company.subscription.status}
                            color={getSubscriptionStatusColor(company.subscription.status) as any}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {company.subscription.users} / {company.subscription.maxUsers}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Math.round((company.subscription.users / company.subscription.maxUsers) * 100)}% used
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {formatStorage(company.storageUsed)} / {formatStorage(company.storageLimit)}
                          </Typography>
                          <Typography variant="caption" color={storagePercentage > 80 ? 'error' : 'text.secondary'}>
                            {storagePercentage}% used
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={company.status}
                          color={getStatusColor(company.status) as any}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewCompany(company)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small">
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Settings">
                          <IconButton size="small" color="primary">
                            <Settings />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
          </>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                General Settings
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Language />
                  </ListItemIcon>
                  <ListItemText primary="Default Language" secondary="English (US)" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText primary="Default Timezone" secondary="America/New_York" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Payment />
                  </ListItemIcon>
                  <ListItemText primary="Default Currency" secondary="USD ($)" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Preferences
              </Typography>
              <List>
                <ListItem>
                  <ListItemText primary="Auto-backup" secondary="Automatically backup data daily" />
                  <Switch defaultChecked />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Email Notifications" secondary="Send system notifications via email" />
                  <Switch defaultChecked />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Maintenance Mode" secondary="Enable during system updates" />
                  <Switch />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Security Settings
              </Typography>
              <List>
                <ListItem>
                  <ListItemText primary="Two-Factor Authentication" secondary="Require 2FA for all admin accounts" />
                  <Switch defaultChecked />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Password Policy" secondary="Enforce strong password requirements" />
                  <Switch defaultChecked />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Session Timeout" secondary="Auto-logout after 30 minutes of inactivity" />
                  <Switch defaultChecked />
                </ListItem>
                <ListItem>
                  <ListItemText primary="IP Restrictions" secondary="Limit access to specific IP ranges" />
                  <Switch />
                </ListItem>
              </List>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Audit & Compliance
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Security />
                  </ListItemIcon>
                  <ListItemText primary="Audit Logs" secondary="Track all system activities" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Info />
                  </ListItemIcon>
                  <ListItemText primary="Compliance Reports" secondary="Generate monthly compliance reports" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Warning />
                  </ListItemIcon>
                  <ListItemText primary="Security Alerts" secondary="Monitor for suspicious activities" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Backup Configuration
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Backup />
                  </ListItemIcon>
                  <ListItemText primary="Daily Backups" secondary="Automated daily system backups" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CloudUpload />
                  </ListItemIcon>
                  <ListItemText primary="Cloud Storage" secondary="Store backups in secure cloud storage" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Update />
                  </ListItemIcon>
                  <ListItemText primary="Retention Policy" secondary="Keep backups for 90 days" />
                </ListItem>
              </List>
              <Button variant="contained" startIcon={<Backup />} sx={{ mt: 2 }}>
                Create Backup Now
              </Button>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Storage Management
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Total system storage usage: 85.2 GB / 500 GB (17%)
              </Alert>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Storage />
                  </ListItemIcon>
                  <ListItemText primary="Database" secondary="45.3 GB" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CloudUpload />
                  </ListItemIcon>
                  <ListItemText primary="File Uploads" secondary="32.1 GB" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Backup />
                  </ListItemIcon>
                  <ListItemText primary="Backups" secondary="7.8 GB" />
                </ListItem>
              </List>
              <Button variant="outlined" startIcon={<Delete />} sx={{ mt: 2 }}>
                Clean Up Storage
              </Button>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* View Company Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Company Details - {selectedCompany?.name}
        </DialogTitle>
        <DialogContent>
          {selectedCompany && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Legal Name
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompany.legalName}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tax ID
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompany.taxId}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Industry
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompany.industry}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Address
                </Typography>
                <Typography variant="body1">
                  {selectedCompany.address.street}<br />
                  {selectedCompany.address.city}, {selectedCompany.address.state} {selectedCompany.address.zipCode}<br />
                  {selectedCompany.address.country}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompany.contact.phone}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompany.contact.email}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Website
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompany.contact.website}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subscription Plan
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompany.subscription.plan}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Users
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompany.subscription.users} / {selectedCompany.subscription.maxUsers}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Expiry Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedCompany.subscription.expiryDate)}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Projects
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompany.totalProjects}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Storage Used
                  </Typography>
                  <Typography variant="body1">
                    {formatStorage(selectedCompany.storageUsed)} / {formatStorage(selectedCompany.storageLimit)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedCompany.createdDate)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button startIcon={<PersonAdd />}>
            Add User
          </Button>
          <Button variant="contained" startIcon={<Edit />}>
            Edit Company
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyManagement;